import * as THREE from "three";
import * as CANNON from "cannon-es";
import { Signal } from "@engine";
import type { PhysicsWorld } from "@engine";
import { PLAYER_RADIUS } from "../config/tunnel";
import {
  isPickupBody,
  isWallBody,
  pickupInstanceOfBody,
  pickupTypeOfBody,
} from "../level/SegmentFactory";
import type { PickupInstance } from "../level/SegmentFactory";
import type { PickupType } from "../level/types";

const FALL_DEATH_Y = -2.5;
const SIDE_DEATH_X = PLAYER_RADIUS * 4;
const PIN_STIFFNESS_X = 80;
const DAMPING_X = 6;
// Soft Y pin that only engages when the ball is at or above its rest
// height OR has any upward velocity. This absorbs spurious upward
// impulses (notably the small bounce caused by wall bodies sweeping
// past the player when the world snaps to a new shape on a gate)
// without ever fighting the gap-fall: as soon as py drops below 0 with
// vy <= 0 the pin disengages and gravity owns the descent.
const PIN_STIFFNESS_Y = 60;
const DAMPING_Y = 12;
// Brief invulnerability after a shield consume so the same wall doesn't
// immediately re-trigger another lethal contact next physics step.
const SHIELD_INVULN_S = 0.35;
// Last-chance save: if the player rotated within this many seconds of
// the lethal wall hit, count it as a frantic save (combo break, brief
// invuln) instead of dying. The cooldown prevents the save from
// trivialising a stretch of dense walls. The window is now an instance
// field (DEFAULT here) so DifficultyService can scale it via
// setLastChanceWindow at startGame: Easy ~0.20s, Hard ~0.09s.
const LAST_CHANCE_WINDOW_DEFAULT_S = 0.14;
const LAST_CHANCE_COOLDOWN_S = 3.0;

export interface PickupHitEvent {
  type: PickupType;
  body: CANNON.Body;
  pickup: PickupInstance | null;
}

export class Player {
  readonly mesh: THREE.Mesh;
  readonly body: CANNON.Body;
  readonly onDied = new Signal<void>();
  readonly onWallContact = new Signal<CANNON.Body>();
  readonly onPickup = new Signal<PickupHitEvent>();
  // Emitted when a charged shield absorbs a lethal hit. Game layer can
  // flash the HUD and burst sparks.
  readonly onShieldConsumed = new Signal<void>();
  // Emitted when a lethal wall hit is converted to a graze because
  // the player rotated within the last-chance window. Game layer
  // pops the "CLOSE!" feedback, breaks the combo, and bursts sparks.
  readonly onLastChanceSave = new Signal<void>();

  private physics: PhysicsWorld;
  private alive = true;
  private shieldCharged = false;
  private invulnRemaining = 0;
  // ReverseGateFx flips this true during the Reverse Gate boss; the
  // soft Y-pin uses pinSign so the rest position is at +PLAYER_RADIUS
  // (top of the cavity) instead of -PLAYER_RADIUS (bottom). Gravity
  // itself flips at the world level (PhysicsWorld.setGravity), so this
  // pin polarity has to follow.
  private gravityFlipped = false;
  // Tracks the most recent rotate input time in AudioContext seconds
  // (game layer pushes via noteRotateInput). The kill check compares
  // this to the current audio time (also pushed via syncAudioNow) to
  // decide if a save is in window. Player avoids reading the audio
  // context directly so it stays decoupled from AudioEngine.
  private lastRotateAtSec = -Infinity;
  private lastSaveAtSec = -Infinity;
  private currentAudioNowSec = -Infinity;
  // Difficulty-scaled last-chance window. DifficultyService writes via
  // setLastChanceWindow at startGame; reset() restores the default so a
  // fresh menu run starts neutral if the wiring ever skips a tick.
  private lastChanceWindowS = LAST_CHANCE_WINDOW_DEFAULT_S;

  constructor(physics: PhysicsWorld) {
    this.physics = physics;

    const geo = new THREE.SphereGeometry(PLAYER_RADIUS, 24, 18);
    const mat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      emissive: 0xffffff,
      emissiveIntensity: 0.5,
      roughness: 0.2,
      metalness: 0.0,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.position.set(0, 0, 0);

    this.body = new CANNON.Body({
      mass: 1,
      shape: new CANNON.Sphere(PLAYER_RADIUS),
      linearDamping: 0.2,
      angularDamping: 0.4,
      position: new CANNON.Vec3(0, 0, 0),
    });
    (this.body as CANNON.Body & { __tag?: string }).__tag = "player";
    this.body.allowSleep = false;
    physics.addBody(this.body);

    physics.onBeginContact.on((ev) => {
      if (!this.alive) return;
      // Pickup branch: pickups are sensors (collisionResponse = false),
      // so they fire begin-contact without resolving any forces. We
      // route them to onPickup BEFORE wall handling so a pickup contact
      // never accidentally triggers the kill-speed branch.
      let pickupBody: CANNON.Body | null = null;
      if (ev.bodyA === this.body && isPickupBody(ev.bodyB)) pickupBody = ev.bodyB;
      else if (ev.bodyB === this.body && isPickupBody(ev.bodyA)) pickupBody = ev.bodyA;
      if (pickupBody) {
        const type = pickupTypeOfBody(pickupBody);
        if (type) {
          this.onPickup.emit({
            type,
            body: pickupBody,
            pickup: pickupInstanceOfBody(pickupBody),
          });
        }
        return;
      }

      let wall: CANNON.Body | null = null;
      if (ev.bodyA === this.body && isWallBody(ev.bodyB)) wall = ev.bodyB;
      else if (ev.bodyB === this.body && isWallBody(ev.bodyA)) wall = ev.bodyA;
      if (wall) {
        this.onWallContact.emit(wall);
        this.onWallHit(wall);
      }
    });
  }

  reset(): void {
    this.alive = true;
    this.body.position.set(0, 0, 0);
    this.body.velocity.set(0, 0, 0);
    this.body.angularVelocity.set(0, 0, 0);
    this.body.quaternion.set(0, 0, 0, 1);
    this.body.aabbNeedsUpdate = true;
    this.shieldCharged = false;
    this.invulnRemaining = 0;
    this.lastRotateAtSec = -Infinity;
    this.lastSaveAtSec = -Infinity;
    this.currentAudioNowSec = -Infinity;
    this.gravityFlipped = false;
    this.lastChanceWindowS = LAST_CHANCE_WINDOW_DEFAULT_S;
  }

  // MorphHexGame.startGame writes the difficulty-scaled save window
  // here. Tightening it (Hard) makes graze-saves feel like real
  // micro-skill; widening it (Easy) gives new players a generous
  // forgiving second of reaction time.
  setLastChanceWindow(seconds: number): void {
    this.lastChanceWindowS = Math.max(0, seconds);
  }

  // Mirror the Y-pin sign during the Reverse Gate boss. ReverseGateFx
  // is the only caller; it clears() back to false on bossCleared and
  // we also clear in reset() so a respawn never inherits the flip.
  setGravityFlipped(flipped: boolean): void {
    this.gravityFlipped = flipped;
  }

  // Mutate the existing sphere material in place so cosmetic swaps
  // don't churn GPU resources or invalidate the existing geometry
  // binding. Color + emissive accept hex numbers; the rest is a
  // straight assign on the MeshStandardMaterial fields.
  setMaterialConfig(cfg: {
    color: number;
    emissive: number;
    emissiveIntensity: number;
    roughness: number;
    metalness: number;
  }): void {
    const mat = this.mesh.material as THREE.MeshStandardMaterial;
    mat.color.setHex(cfg.color);
    mat.emissive.setHex(cfg.emissive);
    mat.emissiveIntensity = cfg.emissiveIntensity;
    mat.roughness = cfg.roughness;
    mat.metalness = cfg.metalness;
    mat.needsUpdate = true;
  }

  // MorphHexGame calls this on every rotate input with the current
  // AudioContext.currentTime so the last-chance save check can be
  // expressed in audio time (same clock as wall contacts -- both are
  // observed inside the physics step which happens after the input
  // has been processed in the same frame).
  noteRotateInput(audioNowSec: number): void {
    this.lastRotateAtSec = audioNowSec;
  }

  // MorphHexGame pushes the current audio time once per frame BEFORE
  // physics.step so any onWallHit fired inside that step has a fresh
  // value to compare against. Decouples Player from AudioEngine.
  syncAudioNow(audioNowSec: number): void {
    this.currentAudioNowSec = audioNowSec;
  }

  isAlive(): boolean {
    return this.alive;
  }

  hasShield(): boolean {
    return this.shieldCharged;
  }

  setShield(active: boolean): void {
    this.shieldCharged = active;
  }

  consumeShield(): boolean {
    if (!this.shieldCharged) return false;
    this.shieldCharged = false;
    this.invulnRemaining = SHIELD_INVULN_S;
    this.onShieldConsumed.emit();
    return true;
  }

  update(dt: number): void {
    if (!this.alive) {
      this.mesh.position.set(this.body.position.x, this.body.position.y, this.body.position.z);
      return;
    }

    if (this.invulnRemaining > 0) {
      this.invulnRemaining = Math.max(0, this.invulnRemaining - dt);
    }

    const px = this.body.position.x;
    const py = this.body.position.y;
    if (this.body.position.z !== 0) {
      this.body.position.z = 0;
      this.body.aabbNeedsUpdate = true;
    }
    this.body.velocity.z = 0;

    // Keep player pinned at world x=0 (the world rotates around the
    // tunnel axis, but the player should always sit on the face that
    // is currently under world origin).
    this.body.force.x += -px * PIN_STIFFNESS_X - this.body.velocity.x * DAMPING_X;

    // Soft Y pin: only engage when the ball is being lifted off the
    // floor (py > 0) or already has upward velocity. When the floor
    // disappears under the player (gap), py drops below 0 with no
    // upward velocity, the pin stays off, and gravity pulls the ball
    // down for the FALL_DEATH_Y check below. This kills the tiny
    // visual jump that used to happen when wall bodies sweep past the
    // player during a gate shape-snap.
    //
    // Reverse Gate flips gravity to +Y (PhysicsWorld), so the rest
    // sign flips with it: the pin should engage when py < 0 (ball
    // pushed below origin) instead of py > 0. We keep the same
    // damping/stiffness shape so the feel is preserved.
    const vy = this.body.velocity.y;
    if (this.gravityFlipped) {
      if (py < 0 || vy < 0) {
        this.body.force.y += -py * PIN_STIFFNESS_Y - vy * DAMPING_Y;
      }
    } else {
      if (py > 0 || vy > 0) {
        this.body.force.y += -py * PIN_STIFFNESS_Y - vy * DAMPING_Y;
      }
    }

    // Death-Y check has to flip with gravity too: in normal play the
    // ball falls into -Y; under reverse gravity it falls into +Y.
    const overY = this.gravityFlipped
      ? py > -FALL_DEATH_Y
      : py < FALL_DEATH_Y;
    if (overY || Math.abs(px) > SIDE_DEATH_X) {
      // Falling into a gap is NEVER absorbed by shield; it's a navigation
      // failure, not a wall graze. Same for sideways escapes.
      this.kill();
    }

    this.mesh.position.set(this.body.position.x, this.body.position.y, this.body.position.z);
    this.mesh.quaternion.set(
      this.body.quaternion.x,
      this.body.quaternion.y,
      this.body.quaternion.z,
      this.body.quaternion.w
    );
    void dt;
  }

  private onWallHit(_wall: CANNON.Body): void {
    // Most contacts are the benign "ball resting on floor" case. We only
    // care about high-energy hits (side wall during rotation, or falling
    // onto a wall after a gap). Falling/side-displacement death is also
    // covered by FALL_DEATH_Y / SIDE_DEATH_X in update().
    const speed = Math.hypot(this.body.velocity.x, this.body.velocity.y);
    if (speed > 7) {
      // Brief invuln window after a shield consume swallows the next
      // contact silently so the same wall doesn't double-tap.
      if (this.invulnRemaining > 0) return;
      if (this.consumeShield()) {
        // Push the ball gently back toward x=0 so the X-pin doesn't have
        // to fight a deeply embedded body next frame.
        this.body.velocity.x *= 0.2;
        return;
      }
      // Last-chance save: we'd otherwise die here, but if the player
      // rotated very recently AND we're not still in the cooldown
      // from the previous save, treat it as a graze. The cooldown
      // matters because dense wall stretches could otherwise trigger
      // the save 4-5 times in a row and trivialise the whole run.
      if (this.tryLastChanceSave()) return;
      this.kill();
    }
  }

  private tryLastChanceSave(): boolean {
    const now = this.currentAudioNowSec;
    if (!Number.isFinite(now)) return false;
    if (now - this.lastSaveAtSec < LAST_CHANCE_COOLDOWN_S) return false;
    if (now - this.lastRotateAtSec > this.lastChanceWindowS) return false;
    // Soak the impact: brief invuln so the same wall can't immediately
    // re-fire, push back toward x=0 so the pin doesn't have to fight
    // a deeply embedded body, and emit so the game layer can flash.
    this.invulnRemaining = SHIELD_INVULN_S;
    this.body.velocity.x *= 0.2;
    this.lastSaveAtSec = now;
    this.onLastChanceSave.emit();
    return true;
  }

  private kill(): void {
    if (!this.alive) return;
    this.alive = false;
    this.onDied.emit();
  }

  dispose(): void {
    this.physics.removeBody(this.body);
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.onDied.clear();
    this.onWallContact.clear();
    this.onPickup.clear();
    this.onShieldConsumed.clear();
    this.onLastChanceSave.clear();
  }
}
